import React, { useEffect, useState } from 'react';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { coworkForestaApi } from '../../lib/api';
import { LiquidacionTrimestralTable } from './LiquidacionTrimestralTable';

const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 });
  return `${v < 0 ? '-' : ''}$${s}`;
};

const EGRESO_ROWS = [
  ['iibb', 'IIBB'],
  ['expensas', 'Expensas'],
  ['edemsa', 'Edemsa'],
  ['municipalidad_agua', 'Municipalidad y Agua'],
  ['impuesto_inmobiliario', 'Impuesto inmobiliario'],
  ['servicio_limpieza', 'Servicio de limpieza'],
  ['marketing_pauta', 'Marketing y pauta'],
  ['puesta_punto_cowork', 'Legales/contables'],
  ['mantenimiento_general', 'Mantenimiento general'],
];

const SectionBand = ({ children }) => (
  <div className="bg-[#2f5e4e] text-white text-sm font-bold uppercase tracking-wider px-4 py-2">{children}</div>
);

const Line = ({ label, value, bold = false }) => (
  <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-50">
    <span className={`text-sm text-slate-700 ${bold ? 'font-bold' : ''}`}>{label}</span>
    <span className={`text-sm font-mono tabular-nums ${bold ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{value}</span>
  </div>
);

/**
 * Vista del inversor del pool Cowork (portal propietario).
 * - Resumen: reporte de rendición (encabezado + resumen general + TU participación).
 * - Detalle: tabla completa del trimestre (ingresos y egresos), solo lectura.
 * Los datos son solo de trimestres PUBLICADOS y la participación/monto corresponden
 * únicamente al propietario logueado.
 */
export const CoworkInversorView = ({ edificioId, activeTab = 'resumen' }) => {
  const [loading, setLoading] = useState(true);
  const [liqs, setLiqs] = useState([]);
  const [sel, setSel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await coworkForestaApi.misLiquidacionesTrimestrales();
        const all = (r.data?.liquidaciones || []).filter((l) => !edificioId || l.edificio_id === edificioId);
        setLiqs(all);
        if (all.length > 0) setSel(all[0].trimestre_key);
      } catch (e) {
        setLiqs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [edificioId]);

  const current = liqs.find((l) => l.trimestre_key === sel);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-500" data-testid="cowork-inv-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  if (liqs.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 py-14 text-center" data-testid="cowork-inv-empty">
        <FileSpreadsheet className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Todavía no hay rendiciones trimestrales publicadas para tu pool Cowork.</p>
      </div>
    );
  }

  const liq = current;
  const comp = liq?.computados;
  const filas = liq?.filas || {};
  const mi = liq?.mi_participacion || { puestos: 0, participacion_pct: 0, monto_trimestre: 0 };

  return (
    <div className="space-y-4" data-testid="cowork-inv-view">
      {/* Selector de trimestre + ocupación */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          data-testid="cowork-inv-periodo-selector"
        >
          {liqs.map((l) => (
            <option key={l.trimestre_key} value={l.trimestre_key}>{l.trimestre_label || l.trimestre_key}</option>
          ))}
        </select>
        <span className="text-sm font-medium text-slate-700" data-testid="cowork-inv-ocupacion">
          Ocupación: {current?.ocupacion_pct || 0}%
        </span>
      </div>

      {activeTab === 'detalle' ? (
        <div data-testid="cowork-inv-detalle">
          <LiquidacionTrimestralTable
            filas={liq.filas}
            feePct={liq.fee_pct}
            mesesLabels={liq.meses_labels}
            editable={false}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="cowork-inv-resumen">
          {/* Resumen general del trimestre */}
          <SectionBand>Resumen general del trimestre</SectionBand>
          <div>
            <Line label="Ingresos por membresías" value={fmt(filas.ingresos_membresias?.trimestre)} bold />
            <div className="px-4 py-1.5 border-b border-slate-50"><span className="text-sm font-bold text-slate-700">Egresos operativos:</span></div>
            {EGRESO_ROWS.map(([k, lbl]) => <Line key={k} label={lbl} value={fmt(filas[k]?.trimestre)} />)}
            <Line label="Total egresos operativos" value={fmt(comp?.total_egresos_operativos?.trimestre)} bold />
            <Line label="Gastos extraordinarios" value={fmt(filas.gastos_extraordinarios?.trimestre)} />
            <Line label={`Fee de gestión GCSAS (${liq.fee_pct || 8}%)`} value={fmt(comp?.fee_gestion?.trimestre)} />
            <Line label="Recupero expensas usuarios" value={fmt(filas.recupero_expensas?.trimestre)} />
            <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50">
              <span className="text-sm font-bold text-emerald-800 uppercase">Resultado neto a distribuir</span>
              <span className="text-base font-bold font-mono tabular-nums text-emerald-800">{fmt(comp?.resultado_neto_distribuir?.trimestre)}</span>
            </div>
          </div>

          {/* Tu participación */}
          <div className="mt-4"><SectionBand>Tu participación</SectionBand></div>
          <div>
            <Line label="Puestos" value={mi.puestos} />
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-50">
              <span className="text-sm text-slate-700">Participación:</span>
              <span className="text-sm font-mono tabular-nums text-slate-700" data-testid="cowork-inv-participacion">
                {(mi.participacion_pct || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100">
              <span className="text-sm font-bold text-slate-800">Monto del trimestre:</span>
              <span className="text-base font-bold font-mono tabular-nums text-slate-900" data-testid="cowork-inv-mi-monto">{fmt(mi.monto_trimestre)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoworkInversorView;
